import { Fragment } from 'react'

import Loader from 'common/components/loaders/Loader'
import { formatDate } from 'common/helpers/date'
import { getAllElements } from 'features/unifiedFlow/contentPage/helpers'

import RightPanelHeading from '../../components/layout/RightPanelHeading'
import { useContent } from '../../hooks'
import useGetLogsQuery from '../centerSection/api/queries/useGetLogsQuery'

import ExpandedIcons from './ExpandedIcons'
import { ActivePanel, HandleActivePanelChange } from './RightSection'

type Props = {
  activePanel: ActivePanel | undefined
  handleActivePanelChange: HandleActivePanelChange
}

const IntegrationLogSection = ({
  activePanel,
  handleActivePanelChange,
}: Props) => {
  const { content } = useContent()
  const { data: logs, logsState } = useGetLogsQuery(content?._id)

  const allElements = getAllElements(content)

  return (
    <div
      className="h-full flex flex-col p-6 flex-1"
      data-testid="integration-log-panel"
    >
      <RightPanelHeading
        text="Logs"
        className="mb-3"
        icons={
          <ExpandedIcons
            activePanel={activePanel}
            handleActivePanelChange={handleActivePanelChange}
          />
        }
      />
      {
        {
          loading: (
            <div className="flex items-center justify-center h-full">
              <Loader />
            </div>
          ),
          empty: (
            <div className="flex items-center justify-center h-full">
              <span className="text-base-700">No logs available</span>
            </div>
          ),
          hasLogs: (
            <>
              {logs?.map(({ elementId, entries }, index) => {
                const element = allElements.find(
                  (element) => element.element_id === elementId
                )

                const elementName = element?.display_name || element?.name

                return (
                  <div
                    key={index}
                    className="flex flex-col mt-6 p-6 bg-gold-40"
                    data-testid="integration-log-section"
                  >
                    <span
                      className="uppercase font-medium text-base-700"
                      data-testid="element-name"
                    >
                      {elementName}
                    </span>
                    <hr className="my-4 border-gold-400" />
                    {entries.map(
                      ({ files, providerName, sendDate, user }, index) => (
                        <Fragment key={index}>
                          {index > 0 && (
                            <hr className="my-4 text-coolGray-300" />
                          )}
                          <div className="flex gap-2">
                            <div>
                              <span className="block text-gold-700 text-sm">
                                User:
                              </span>
                              <span className="block text-gold-700 text-sm">
                                Sent date:
                              </span>
                              <span className="block text-gold-700 text-sm mt-6">
                                Files:
                              </span>
                            </div>
                            <div className="text-sm text-base-700">
                              <span className="block" data-testid="user">
                                {user}
                              </span>
                              <span className="block" data-testid="sent-date">
                                {formatDate(sendDate)}
                              </span>
                              <div className="mt-6">
                                <span
                                  className="block font-bold"
                                  data-testid="provider-name"
                                >
                                  {providerName}
                                </span>
                                <ul
                                  className="list-disc list-inside pl-2"
                                  data-testid="asset-list"
                                >
                                  {files.map((file, index) => (
                                    <li key={index}>{file}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </Fragment>
                      )
                    )}
                  </div>
                )
              })}
            </>
          ),
        }[logsState]
      }
    </div>
  )
}

export default IntegrationLogSection
